/**
 * 経費申請(v3.7改修)。
 * 縦2列の狭いレイアウト(max-width 760px)。
 *
 * 項目構成: 会社 / 事業部 / 拠点・場所(2段選択・任意) / 科目①② / 計上日 /
 * 支払い予定金額(税込み・自動カンマ) / 支払予定日 / 支払い者(固定3択・表示名は設定で変更可) /
 * 支払い方法(現金・振込・クレカ) / 添付(領収書/振込明細/請求書。D&D・ペースト対応) /
 * 支払先(自由記述) / 摘要 / 備考(任意)
 *
 * - 「経理に依頼する」を選んだ場合は添付必須(firestore.rules と二重実装)。現金払いは任意(2026-09-17 改修依頼 D)
 * - 「その他」を選んだ項目の内容は摘要に記載する(摘要は常に必須)
 * - 下書きは複数保存でき、申請一覧の「続きを入力」から ?draft=<ID> で開く(2026-09-17 改修依頼 A)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { todayStr } from '@core/lib/date';
import { parseAmount } from '@core/lib/money';
import { uploadFiles } from '@core/expense/attachments';
import FileDropZone from '@core/expense/FileDropZone';
import SuggestInput from '@core/components/SuggestInput';
import { childrenOf, fetchHierarchyNodes } from '@core/masters/hierarchyRepo';
import {
  createExpenseApplication,
  fetchMyApplications,
  managerCheckSkipped,
  newApplicationId,
  resubmitApplication,
  roleOf,
} from './applicationRepo';
import { fetchFlowPatterns, findPattern, type FlowPattern } from './flowPatterns';
import { deleteDraft, loadDraft, saveDraft } from './draftRepo';
import { reportFlowResult } from './flowResult';
import { weekendOrHoliday } from '@core/lib/jpHolidays';
import Modal from '@core/components/Modal';
import DateField from '@core/components/DateField';
import { tsToShortDate } from '@core/lib/date';
import { reconnectNow } from '@core/lib/firebaseReconnect';
import {
  APPLY_PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type DepartmentId,
  type HierarchyNode,
  type Payer,
  type PaymentMethod,
  type Transaction,
} from '@core/lib/types';

/** 「その他」の選択値(拠点・場所用。保存時は null にし、内容は摘要に書く) */
const OTHER = '__other__';

/**
 * 入力に応じて自動で高さが伸びるテキストエリア(摘要・備考)。
 * 改行で行が増え、再申請時など初期値が複数行でも開いた時点で全体が見える
 */
function AutoGrowTextarea({
  value,
  onChange,
  placeholder,
  rows = 1,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  const resize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  };
  return (
    <textarea
      ref={resize}
      value={value}
      rows={rows}
      style={{
        width: '100%',
        padding: '9px 11px',
        border: '1px solid var(--line)',
        borderRadius: 6,
        // 文字サイズは指定しない(2026-08-28): CSSの共通ルール(PC=13.5px/スマホ=16px)に
        // 任せ、他の入力欄・プレースホルダーと大きさを揃える
        fontFamily: 'inherit',
        resize: 'none',
        overflow: 'hidden',
        lineHeight: 1.6,
      }}
      onChange={(e) => {
        onChange(e.target.value);
        resize(e.target);
      }}
      placeholder={placeholder}
    />
  );
}

/** 入力中の金額文字列を3桁カンマ付きに整形する(数字以外は取り除く) */
function formatAmountInput(raw: string): string {
  const digits = raw
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/\D/g, '');
  if (digits === '') return '';
  return Number(digits).toLocaleString('ja-JP');
}

export default function ExpenseApplyPage() {
  const { user } = useAuth();
  const { companies, departments, accounts, subAccountsOf, fiscal, defaultCompanyId, payerLabels } = useMasters();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  /** 差戻しからの再申請(申請一覧から遷移) */
  const editing = (location.state as { transaction?: Transaction } | null)?.transaction ?? null;

  const defaultDept = (user?.primary_department_id ?? 'honbu') as DepartmentId;

  const [companyId, setCompanyId] = useState(editing?.company_id ?? defaultCompanyId);
  const [departmentId, setDepartmentId] = useState<DepartmentId>(editing?.department_id ?? defaultDept);
  const [nodes, setNodes] = useState<HierarchyNode[]>([]);
  /** 拠点/場所(1本のプルダウン)。「紀伊国屋」「紀伊国屋/横浜」のように拠点単体と拠点+場所を並べる */
  const [locationId, setLocationId] = useState(editing?.hierarchy_node_id ?? '');
  const [accountId, setAccountId] = useState(editing?.account_id ?? '');
  const [subAccountId, setSubAccountId] = useState(editing?.sub_account_id ?? '');
  const [postingDate, setPostingDate] = useState(editing?.posting_date ?? todayStr());
  const [paymentDate, setPaymentDate] = useState(editing?.payment_date ?? '');
  const [amountText, setAmountText] = useState(editing ? formatAmountInput(String(editing.amount)) : '');
  const [payer, setPayer] = useState<Exclude<Payer, null>>(editing?.payer ?? 'self');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>(editing?.payment_method ?? '');
  const [counterparty, setCounterparty] = useState(editing?.counterparty ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [note, setNote] = useState(editing?.note ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  /**
   * 送信が長引いたときの案内(2026-09-30 指摘: 「送信中」のまま動かないように見える)。
   * 送信そのものは続いているので、止まったと思って画面を閉じたり二重に送ったりしないよう伝える
   */
  const [sendingSlow, setSendingSlow] = useState(false);
  /** 送信中に電波が切れた(Firestoreはつながるまで待ち続けるので、その旨を伝える) */
  const [sendingOffline, setSendingOffline] = useState(false);
  useEffect(() => {
    if (!busy) {
      setSendingSlow(false);
      setSendingOffline(false);
      return;
    }
    // 5秒たっても終わらないときは、つなぎ直しを1回試す(再接続の待ち時間を明けるため)。
    // 送信待ちの内容は端末内に残っているので、つながり次第そのまま送られる
    const nudge = setTimeout(() => void reconnectNow(), 5000);
    const timer = setTimeout(() => setSendingSlow(true), 8000);
    const goOffline = () => setSendingOffline(true);
    const goOnline = () => setSendingOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      clearTimeout(nudge);
      clearTimeout(timer);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, [busy]);
  /** フローパターン(§2)。支払方法ごとに選べる支払者を決める */
  const [patterns, setPatterns] = useState<FlowPattern[]>([]);
  /** 支払先の入力候補(自分の過去申請から。自由記述+候補表示。2026-08-27 復活) */
  const [counterpartyOptions, setCounterpartyOptions] = useState<string[]>([]);
  /** 振込×支払予定日が土日祝のときの確認(■13)。値は「土曜日」「祝日(◯◯)」等 */
  const [holidayWarning, setHolidayWarning] = useState<string | null>(null);
  /** 開いている下書き(2026-09-17 改修依頼 A)。URLの ?draft=<ID> と対応する。null は新しい入力 */
  const [searchParams, setSearchParams] = useSearchParams();
  const draftParam = editing ? null : searchParams.get('draft');
  const [draftId, setDraftId] = useState<string | null>(null);
  /**
   * この画面自身がURLの ?draft= を書き換えたときは、下書きの読み込み・入力の初期化をしない
   * (保存直後に読み直して添付の選択が消える・申請直後に会社や事業部まで初期化される、を防ぐ)
   */
  const ownDraftParamChangeRef = useRef(false);
  function replaceDraftParam(id: string | null) {
    if ((id ?? null) === (draftParam ?? null)) return;
    ownDraftParamChangeRef.current = true;
    setSearchParams(id ? { draft: id } : {}, { replace: true });
  }

  /**
   * 科目①の候補(2026-08-26): 「表示する事業部」にチェックのある科目のみ表示する。
   * チェックが1つもない科目は経費申請に出ない。「その他」事業部ではチェックのある科目をすべて表示
   */
  const expenseAccounts = useMemo(
    () =>
      accounts
        .filter(
          (a) =>
            a.active &&
            (a.department_ids?.length ?? 0) > 0 &&
            (departmentId === 'sonota' || a.department_ids!.includes(departmentId)),
        )
        .sort((a, b) => a.display_order - b.display_order),
    [accounts, departmentId],
  );
  // 事業部を変えて選択中の科目が対象外になったら選び直し
  useEffect(() => {
    if (accountId && !expenseAccounts.some((a) => a.id === accountId)) {
      setAccountId('');
      setSubAccountId('');
    }
  }, [expenseAccounts, accountId]);

  useEffect(() => {
    fetchFlowPatterns()
      .then(setPatterns)
      .catch(() => setPatterns([]));
  }, []);

  /** 入力をすべて初期状態に戻す(添付も外す) */
  function resetForm() {
    setCompanyId(defaultCompanyId);
    setDepartmentId(defaultDept);
    setLocationId('');
    setAccountId('');
    setSubAccountId('');
    setPostingDate(todayStr());
    setPaymentDate('');
    setAmountText('');
    setPayer('self');
    setPaymentMethod('');
    setCounterparty('');
    setDescription('');
    setNote('');
    setFiles([]);
  }

  // 下書きの復元(申請一覧の「続きを入力」から ?draft=<ID> で開いたとき。2026-09-17 改修依頼 A)。
  // 以前は1人1件の下書きを自動で復元していたが、複数持てるようにしたため、選んだ下書きだけを開く
  useEffect(() => {
    if (!user || editing) return;
    if (ownDraftParamChangeRef.current) {
      ownDraftParamChangeRef.current = false;
      return;
    }
    if (!draftParam) {
      // 下書きを開いたまま、メニューから新しい入力に移ったときは空にする
      if (draftId) {
        setDraftId(null);
        resetForm();
      }
      return;
    }
    if (draftParam === draftId) return;
    let alive = true;
    loadDraft(user.id, draftParam)
      .then((d) => {
        if (!alive) return;
        if (!d) {
          toast('下書きが見つかりませんでした(削除された可能性があります)。新しく入力してください');
          setDraftId(null);
          replaceDraftParam(null);
          return;
        }
        setDraftId(d.id);
        setCompanyId(d.companyId || defaultCompanyId);
        setDepartmentId((d.departmentId || defaultDept) as DepartmentId);
        setLocationId(d.locationId ?? '');
        setAccountId(d.accountId ?? '');
        setSubAccountId(d.subAccountId ?? '');
        setPostingDate(d.postingDate || todayStr());
        setPaymentDate(d.paymentDate ?? '');
        setAmountText(d.amountText ?? '');
        setPayer(d.payer === 'bucho' || d.payer === 'accounting' ? d.payer : 'self');
        setPaymentMethod((d.paymentMethod ?? '') as PaymentMethod | '');
        setCounterparty(d.counterparty ?? '');
        setDescription(d.description ?? '');
        setNote(d.note ?? '');
        setFiles([]);
      })
      .catch((e) => {
        console.error('下書きの読み込みに失敗しました', e);
        if (alive) toast('下書きを読み込めませんでした。通信状態を確認して、申請一覧から開き直してください');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, editing, draftParam]);

  // 下書きは「下書き保存」ボタンを押したときだけ保存する(2026-09-10 指示。
  //  以前は入力の1秒後に自動保存していたため、画面を移っただけで一覧に下書きが残っていた)

  /** 下書き保存(2026-09-17 改修依頼 A: 保存に失敗しても「保存しました」と出ていたため、結果を正しく出す) */
  async function saveAsDraft() {
    if (!user) return;
    setBusy(true);
    try {
      const id = await saveDraft(user.id, draftId, {
        companyId,
        departmentId,
        locationId,
        accountId,
        subAccountId,
        postingDate,
        paymentDate,
        amountText,
        payer,
        paymentMethod,
        counterparty,
        description,
        note,
      });
      setDraftId(id);
      replaceDraftParam(id);
      toast('下書きを保存しました。申請一覧の「続きを入力」から再開できます(添付は保存されません)');
    } catch (e) {
      console.error('下書きの保存に失敗しました', e);
      toast('下書きを保存できませんでした。通信状態を確認して、もう一度「下書き保存」を押してください');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    fetchMyApplications(user.id)
      .then((list) => {
        const names = [...new Set(list.map((t) => t.counterparty).filter((c): c is string => !!c?.trim()))];
        setCounterpartyOptions(names.sort((a, b) => a.localeCompare(b, 'ja')));
      })
      .catch(() => setCounterpartyOptions([]));
  }, [user]);

  /**
   * 支払い者の選択肢はパターン表(§2)から: 役割×支払方法で有効な組合せのみ表示する。
   * 例: 振込→経理のみ / 仮払い→自分のみ / 立替→自分・部門長(管理側は自分のみ)
   */
  const role = user ? roleOf(user) : 'member';

  /**
   * パターン外の選択肢の見せ方(2026-08-26 指示・同日修正):
   * 支払い方法は常に選べるようにし、**支払い者側だけ**を選んだ方法に応じてグレーアウトする
   * (方法→支払い者の順に選ぶ自然な流れにするため。双方向のグレーは分かりづらいので廃止)。
   * 'gray' = グレーアウトして選択不可(現行) / 'hide' = 表示しない(将来の運用変更用)
   */
  const INVALID_OPTION_MODE = 'gray' as 'gray' | 'hide';

  /** その支払い方法が役割として使えるか(どの支払い者とも組合せがなければグレー。現行パターンでは全方法が有効) */
  const methodEnabled = useCallback(
    (m: PaymentMethod) =>
      patterns.length === 0 || patterns.some((pat) => pat.roles.includes(role) && pat.payment_method === m),
    [patterns, role],
  );
  /** その支払い者が有効か。支払い方法が未選択の間は、役割で使い道が1つでもあれば有効 */
  const payerEnabled = useCallback(
    (p: Exclude<Payer, null>) => {
      if (patterns.length === 0) return true;
      if (paymentMethod) return !!findPattern(patterns, role, paymentMethod, p);
      return patterns.some((pat) => pat.roles.includes(role) && pat.payer === p);
    },
    [patterns, role, paymentMethod],
  );
  const payerOptions = useMemo(
    () => (['self', 'bucho', 'accounting'] as const).filter(payerEnabled),
    [payerEnabled],
  );
  // 支払い方法を変えて現在の支払い者が無効になったら、有効な先頭へ自動で寄せる
  // (例: 一般メンバーが「現金」を選ぶと支払い者は「経理」になり、自分・部門長はグレー)
  useEffect(() => {
    if (payerOptions.length > 0 && !payerOptions.includes(payer)) setPayer(payerOptions[0]!);
  }, [payerOptions, payer]);

  const bankTransfer = paymentMethod === 'bank_transfer';

  // 「その他」を選んでいる項目(摘要への記入を必須にする・v3.8 1-1)
  const otherSelected =
    companyId === 'sonota' || departmentId === 'sonota' || accountId === 'sonota-keihi' || locationId === OTHER;

  // 拠点(任意)は選んだ事業部の階層マスタから。1段目=最上位、2段目=その子
  useEffect(() => {
    let alive = true;
    fetchHierarchyNodes(departmentId)
      .then((list) => alive && setNodes(list))
      .catch(() => alive && setNodes([]));
    return () => {
      alive = false;
    };
  }, [departmentId]);

  /** プルダウンの選択肢: 拠点単体 と 拠点/場所 を1本に並べる(例: 紀伊国屋, 紀伊国屋/港北, 紀伊国屋/横浜) */
  const locationOptions = useMemo(() => {
    const sites = childrenOf(nodes, null);
    return sites.flatMap((site) => [
      { id: site.id, label: site.name },
      ...childrenOf(nodes, site.id).map((place) => ({ id: place.id, label: `${site.name}/${place.name}` })),
    ]);
  }, [nodes]);

  // 事業部を変えたら拠点を選び直す / 部門長本人になった場合は「部門長に依頼」を解除
  useEffect(() => {
    setLocationId((prev) => (prev === OTHER || nodes.some((n) => n.id === prev) ? prev : ''));
  }, [nodes]);

  async function submit(holidayOk = false) {
    if (!user) return;
    const amount = parseAmount(amountText);
    if (amount === null) {
      toast('支払い予定金額を正しく入力してください(1円以上の整数)');
      return;
    }
    if (!counterparty.trim()) {
      toast('支払先を入力してください');
      return;
    }
    if (!accountId) {
      toast('科目①を選んでください');
      return;
    }
    // クレカは引き落とし日が後から決まるため、支払い予定日は任意(2026-09-10 指示)。
    // 空欄のときは計上日(購入日)を仮の予定日として持たせる(一覧や並び替えで日付が欠けないように)
    if (!paymentDate && paymentMethod !== 'card') {
      toast('支払予定日を入力してください');
      return;
    }
    if (!paymentMethod) {
      toast('支払い方法を選んでください');
      return;
    }
    if (!description.trim()) {
      toast('摘要は必須です');
      return;
    }
    if (otherSelected && description.trim().length < 2) {
      toast('「その他」を選んだ場合は、その内容を摘要に記入してください');
      return;
    }
    if (bankTransfer && files.length === 0 && (editing?.attachments ?? []).length === 0) {
      toast('「振込」は請求書の添付が必須です');
      return;
    }
    // 現金払い(支払者は必ず経理)は添付を任意にする(2026-09-17 改修依頼 D・解釈1で確定)
    if (
      payer === 'accounting' &&
      paymentMethod !== 'cash' &&
      files.length === 0 &&
      (editing?.attachments ?? []).length === 0
    ) {
      toast('「経理に依頼する」の場合は請求書または振込明細の添付が必須です');
      return;
    }
    // 振込で支払予定日が土日祝の場合は確認ポップアップ(■13・2026-08-28 会議)
    if (bankTransfer && !holidayOk) {
      const hit = weekendOrHoliday(paymentDate);
      if (hit) {
        setHolidayWarning(hit.label);
        return;
      }
    }

    // 「その他」は null 扱い(内容は摘要に記載)
    const hierarchyNodeId = locationId && locationId !== OTHER ? locationId : '';

    // 支払い済み(お金が動いた)申請は再申請の対象外。修正は承認者が「修正(全項目)」から行う(2026-09-07)
    if (editing && editing.paid_by) {
      toast('この申請は支払い済みのため再申請できません。内容の修正は承認者が詳細の「修正(全項目)」から行います');
      return;
    }
    // 電波が届いていないと、送信は「送信中」のまま終わらない(Firestoreはつながるまで待ち続ける)。
    // 押す前に気づけるようにする(2026-09-30)
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      toast('インターネットにつながっていません。電波の届く場所で、もう一度「申請する」を押してください');
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const added = files.length > 0 ? await uploadFiles(editing.id, 'attachments', files, user.id) : [];
        const { warning } = await resubmitApplication({
          transaction: editing,
          patch: {
            departmentId,
            hierarchyNodeId,
            counterparty,
            accountId,
            subAccountId,
            postingDate,
            paymentDate: paymentDate || postingDate,
            amount,
            payer,
            paymentMethod,
            description,
            note,
          },
          addedAttachments: added,
          applicant: user,
          fiscalStartMonth: fiscal.start_month,
        });
        reportFlowResult(
          toast,
          managerCheckSkipped(user, departmentId)
            ? `${editing.code} を再申請しました(部門長確認は省略・承認待ちへ)`
            : `${editing.code} を再申請しました(部門長確認からやり直し)`,
          { warning },
        );
        navigate('/mylist');
      } else {
        const id = newApplicationId();
        const attachments = files.length > 0 ? await uploadFiles(id, 'attachments', files, user.id) : [];
        const { code, startStatus, warning } = await createExpenseApplication({
          id,
          input: {
            companyId,
            departmentId,
            hierarchyNodeId,
            counterparty,
            accountId,
            subAccountId,
            postingDate,
            paymentDate: paymentDate || postingDate,
            amount,
            payer,
            paymentMethod,
            description,
            note,
          },
          attachments,
          applicant: user,
          fiscalStartMonth: fiscal.start_month,
          // 科目マスタは画面が持っているものを渡す(通信を1往復減らす。2026-09-30 送信が遅い件)
          account: accounts.find((a) => a.id === accountId) ?? null,
        });
        const applied =
          startStatus === 'approved'
            ? `${code} を申請しました。部門長・承認は省略され、そのまま支払い待ちです`
            : startStatus === 'manager_checked'
              ? `${code} を申請しました。部門長確認を省略し、承認者の承認待ちです`
              : `${code} を申請しました。部門長の確認待ちです`;
        // 申請できたので、開いていた下書きを消す(失敗しても申請は済んでいるので、その旨だけ伝える)
        let draftLeft = false;
        if (draftId) {
          await deleteDraft(user.id, draftId).catch((e) => {
            console.error('申請後の下書きの削除に失敗しました', e);
            draftLeft = true;
          });
          setDraftId(null);
          replaceDraftParam(null);
        }
        reportFlowResult(
          toast,
          draftLeft ? `${applied}(下書きが一覧に残っています。不要なら削除してください)` : applied,
          { warning },
        );
        setLocationId('');
        setAccountId('');
        setSubAccountId('');
        setPaymentDate('');
        setPaymentMethod('');
        setAmountText('');
        setCounterparty('');
        setDescription('');
        setNote('');
        setFiles([]);
      }
    } catch (e) {
      // 権限ルールの反映前に「現金・添付なし」で申請した場合は、分かる言葉で案内する(2026-09-17 改修依頼 D)
      const code = (e as { code?: string } | null)?.code;
      const denied = code === 'permission-denied';
      if (denied && paymentMethod === 'cash' && files.length === 0) {
        toast('現金払いの添付を任意にする設定がまだ反映されていません。お手数ですが添付して申請してください');
      } else if (code === 'unavailable' || code === 'deadline-exceeded') {
        // 通信が届かないときは、古い名簿で承認ルートを決めてしまわないよう中止している(2026-09-30)
        toast('通信が不安定で送信できませんでした。まだ申請は作られていません。電波の良い場所で、もう一度「申請する」を押してください');
      } else {
        toast(e instanceof Error ? e.message : '申請に失敗しました');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="screen">
      <div className="card" style={{ maxWidth: 760 }}>
        <h3 style={{ display: 'flex', alignItems: 'center' }}>
          {editing ? `経費を再申請する(${editing.code})` : '経費を申請する'}
          {/* 入力のオールクリア(2026-08-28。控えめな配置・確認あり)。開いている下書きも消す */}
          {!editing && (
            <button
              className="txn-link"
              style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 400, color: 'var(--muted)' }}
              disabled={busy}
              onClick={() => {
                if (!window.confirm(draftId ? '入力内容をすべてクリアしますか?(この下書きも削除されます)' : '入力内容をすべてクリアしますか?')) return;
                resetForm();
                if (user && draftId) {
                  const id = draftId;
                  setDraftId(null);
                  replaceDraftParam(null);
                  deleteDraft(user.id, id)
                    .then(() => toast('入力内容をクリアし、下書きを削除しました'))
                    .catch((e) => {
                      console.error('下書きの削除に失敗しました', e);
                      toast('入力内容はクリアしましたが、下書きを削除できませんでした。申請一覧から削除してください');
                    });
                } else {
                  toast('入力内容をクリアしました');
                }
              }}
            >
              入力をクリア
            </button>
          )}
        </h3>

        {/* 差戻しからの再申請: 直近の差戻しコメントを表示(v3.8 4章) */}
        {editing?.reject_reason && (
          <div
            style={{
              margin: '0 0 14px',
              padding: '10px 14px',
              borderRadius: 8,
              background: 'var(--red-bg)',
              color: 'var(--red)',
              fontSize: 13,
            }}
          >
            <b>差戻しコメント</b>
            {editing.rejected_at ? `(${tsToShortDate(editing.rejected_at)})` : ''}: {editing.reject_reason}
          </div>
        )}
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <div>
            <label>申請者</label>
            <input readOnly value={user?.display_name ?? ''} style={{ background: '#EEF2F8', color: 'var(--muted)' }} />
          </div>
          <div>
            <label>
              会社<span className="req">必須</span>
            </label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>
              事業部<span className="req">必須</span>
            </label>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>拠点/場所(任意)</label>
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">—</option>
              {locationOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
              <option value={OTHER}>その他(摘要に記載)</option>
            </select>
          </div>
          <div>
            <label>
              科目①<span className="req">必須</span>
            </label>
            <select
              value={accountId}
              onChange={(e) => {
                setAccountId(e.target.value);
                setSubAccountId('');
              }}
            >
              <option value="">選択してください</option>
              {expenseAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>科目②(任意)</label>
            <select value={subAccountId} disabled={!accountId} onChange={(e) => setSubAccountId(e.target.value)}>
              <option value="">—</option>
              {(accountId ? subAccountsOf(accountId) : []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>
              計上日<span className="req">必須</span>
            </label>
            <DateField value={postingDate} onChange={setPostingDate} />
          </div>
          <div>
            <label>
              支払い予定金額(税込み)<span className="req">必須</span>
            </label>
            <input
              inputMode="numeric"
              placeholder="12,000"
              value={amountText}
              onChange={(e) => setAmountText(formatAmountInput(e.target.value))}
            />
          </div>
          {/* 並び順: 支払い予定日 → 支払い方法 → 支払い者(v3.8 1-5) */}
          <div>
            <label>
              支払い予定日{paymentMethod === 'card' ? <span className="hint">(任意)</span> : <span className="req">必須</span>}
            </label>
            <DateField value={paymentDate} onChange={setPaymentDate} />
            {paymentMethod === 'card' && (
              <div className="hint" style={{ marginTop: 3 }}>
                クレカは引き落とし日が後から決まるため空欄で構いません(支払い時に経理が入力します)
              </div>
            )}
          </div>
          <div>
            <label>
              支払い方法<span className="req">必須</span>
            </label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}>
              <option value="">選択してください</option>
              {APPLY_PAYMENT_METHODS.map((m) => {
                const ok = methodEnabled(m);
                if (!ok && INVALID_OPTION_MODE === 'hide') return null;
                return (
                  <option key={m} value={m} disabled={!ok}>
                    {PAYMENT_METHOD_LABELS[m]}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label>
              支払い者<span className="req">必須</span>
            </label>
            <select value={payer} onChange={(e) => setPayer(e.target.value as Exclude<Payer, null>)}>
              {(['self', 'bucho', 'accounting'] as const).map((p) => {
                const ok = payerEnabled(p);
                if (!ok && INVALID_OPTION_MODE === 'hide') return null;
                return (
                  <option key={p} value={p} disabled={!ok}>
                    {payerLabels[p]}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="fw">
            <label>
              領収書/振込明細/請求書
              {bankTransfer || (payer === 'accounting' && paymentMethod !== 'cash') ? (
                <span className="req">必須</span>
              ) : (
                '(任意)'
              )}
              {bankTransfer && <span className="hint">(振込は請求書の添付が必須です)</span>}
            </label>
            <FileDropZone files={files} onChange={setFiles} />
          </div>
          {/* 支払先(自由記述)。入力すると過去の支払先が部分一致で候補表示される(2026-08-27) */}
          <div className="fw">
            <label>
              支払先<span className="req">必須</span>
            </label>
            <SuggestInput
              value={counterparty}
              onChange={setCounterparty}
              options={counterpartyOptions}
              placeholder="例)〇〇商事"
            />
          </div>
          <div className="fw">
            <label>
              摘要(購入品明細、または受領書の場合は但し書き)<span className="req">必須</span>
            </label>
            <AutoGrowTextarea
              value={description}
              onChange={setDescription}
              placeholder="例)配送用タイヤ4本 購入希望"
            />
          </div>
          <div className="fw">
            <label>備考(任意)</label>
            <AutoGrowTextarea value={note} onChange={setNote} rows={2} placeholder="補足があれば記入(改行可)" />
          </div>
        </div>

        {/* 「支払い入力欄」の案内ボックスは削除(2026-08-28 指示。支払いは支払いページの専用フォームで入力) */}
        <button className="btn-green" onClick={() => void submit()} disabled={busy}>
          {busy ? '送信中…' : editing ? '再申請する' : '申請する'}
        </button>
        {/* 下書き保存(2026-08-26)。複数保存でき、申請一覧の「続きを入力」から再開する(2026-09-17 改修依頼 A) */}
        {!editing && (
          <button className="btn-line" style={{ marginLeft: 10 }} disabled={busy} onClick={() => void saveAsDraft()}>
            下書き保存
          </button>
        )}
        {/* 送信中の案内(2026-09-30)。8秒を過ぎたら「止まっていない」ことを伝える */}
        {busy && (
          <p className="hint" style={{ marginTop: 8 }}>
            {sendingOffline
              ? '電波が切れています。つながり次第、自動で送信されます。この画面を閉じずにお待ちください。'
              : sendingSlow
                ? '通信が遅くなっています。送信は続いていますので、画面を閉じずにお待ちください(二重に申請されないよう、ボタンは押せなくしています)。'
                : '送信しています…'}
          </p>
        )}
        {/* フロー説明の文章は削除(2026-08-28 指示) */}
      </div>

      {/* 振込×土日祝の確認(■13・2026-08-28)。続行 or 日付修正 */}
      {holidayWarning && (
        <Modal onClose={() => setHolidayWarning(null)} width="min(480px, 92vw)" showClose={false}>
          <div style={{ border: '2px solid var(--amber)', background: 'var(--amber-bg)', borderRadius: 8, padding: '16px 18px' }}>
            <div style={{ color: 'var(--amber)', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
              ⚠ 支払予定日が{holidayWarning}です
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.9 }}>
              支払予定日({paymentDate ? paymentDate.replace(/-/g, '/') : ''})は土日祝日ですが、この日付でよろしいですか?
              銀行振込は土日祝日に着金しない場合があります。
            </div>
            {/* ボタン内で文字が折り返さないようにする(2026-08-28 指示) */}
            <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
              <button
                className="btn-green"
                style={{ marginTop: 0, whiteSpace: 'nowrap' }}
                disabled={busy}
                onClick={() => {
                  setHolidayWarning(null);
                  void submit(true);
                }}
              >
                この日付で申請する
              </button>
              <button className="btn-line" style={{ whiteSpace: 'nowrap' }} onClick={() => setHolidayWarning(null)}>
                日付を修正する
              </button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
